import { useEffect, useState } from 'react'
import { fetchTranslationAvailability } from './translationApi'

/**
 * GATE 1 nº2: gating is static (the setting comes from frontEndConfig, once per session) plus a
 * health check at the moments that matter — when the TopBar mounts and when the dialog opens.
 * The result is memoised for ~60 s so those two moments do not double-hit the service, and so
 * remounting the chrome does not generate traffic. There is deliberately no polling.
 */
export const AVAILABILITY_CACHE_TTL_MS = 60000

let cache = { checkedAt: 0, pending: null }

/**
 * Consults the translator availability, reusing the recent answer when there is one.
 * @returns {Promise<boolean>}
 */
export const consultTranslationAvailability = () => {
  const now = Date.now()
  if (cache.pending && now - cache.checkedAt < AVAILABILITY_CACHE_TTL_MS) return cache.pending

  // A failed consult is cached as "unavailable" for the same window: a service that is down
  // should not be retried on every mount either.
  cache = { checkedAt: now, pending: fetchTranslationAvailability().catch(() => false) }
  return cache.pending
}

/**
 * Resolves RN1: the translator is offered only when the global setting is ON *and* the service
 * answers healthy. With the setting OFF nothing is requested at all (RN2/A1).
 *
 * @param {boolean} translatorEnabled global setting, read from frontEndConfig
 * @returns {boolean} whether the tool can be offered
 */
const useTranslationAvailability = (translatorEnabled) => {
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    if (!translatorEnabled) return

    let active = true
    consultTranslationAvailability().then((result) => { if (active) setAvailable(result) })

    return () => { active = false }
  }, [translatorEnabled])

  return available
}

export default useTranslationAvailability
